// src/presentation/pages/ClinicalExamination/sections/CardiovascularRiskFactors.tsx
import React from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface CardiovascularRiskFactorsProps {
  hasHypertension: boolean | null;
  hypertensionYear: string;
  hasDiabetes: boolean | null;
  diabetesYear: string;
  hasHighCholesterol: boolean | null;
  highCholesterolYear: string;
  hasHeartDisease: boolean | null;
  heartDiseaseYear: string;
  onHypertensionChange: (value: boolean) => void;
  onHypertensionYearChange: (value: string) => void;
  onDiabetesChange: (value: boolean) => void;
  onDiabetesYearChange: (value: string) => void;
  onHighCholesterolChange: (value: boolean) => void;
  onHighCholesterolYearChange: (value: string) => void;
  onHeartDiseaseChange: (value: boolean) => void;
  onHeartDiseaseYearChange: (value: string) => void;
}

export const CardiovascularRiskFactors: React.FC<
  CardiovascularRiskFactorsProps
> = ({
  hasHypertension,
  hypertensionYear,
  hasDiabetes,
  diabetesYear,
  hasHighCholesterol,
  highCholesterolYear,
  hasHeartDisease,
  heartDiseaseYear,
  onHypertensionChange,
  onHypertensionYearChange,
  onDiabetesChange,
  onDiabetesYearChange,
  onHighCholesterolChange,
  onHighCholesterolYearChange,
  onHeartDiseaseChange,
  onHeartDiseaseYearChange,
}) => {
  const { t } = useTranslation("patients");
  return (
    <div className="border-gray-200">
      <CardHeader className="pb-3 !px-0">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.cardiovascularRiskFactors.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className=" !px-0 space-y-4">
        {/* Hypertension */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cardiovascularRiskFactors.hypertensionQuestion")}
          </p>
          <RadioGroup
            value={hasHypertension?.toString()}
            onValueChange={(val) => onHypertensionChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="hyper-yes" />
              <Label
                htmlFor="hyper-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="hyper-no" />
              <Label
                htmlFor="hyper-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasHypertension === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="hyper-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cardiovascularRiskFactors.yearLabel")}
              </Label>
              <Input
                id="hyper-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={hypertensionYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onHypertensionYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>

        {/* Diabetes */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cardiovascularRiskFactors.diabetesQuestion")}
          </p>
          <RadioGroup
            value={hasDiabetes?.toString()}
            onValueChange={(val) => onDiabetesChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="diabetes-yes" />
              <Label
                htmlFor="diabetes-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="diabetes-no" />
              <Label
                htmlFor="diabetes-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasDiabetes === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="diabetes-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cardiovascularRiskFactors.yearLabel")}
              </Label>
              <Input
                id="diabetes-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={diabetesYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onDiabetesYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>

        {/* High Cholesterol */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cardiovascularRiskFactors.highCholesterolQuestion")}
          </p>
          <RadioGroup
            value={hasHighCholesterol?.toString()}
            onValueChange={(val) => onHighCholesterolChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="chol-yes" />
              <Label
                htmlFor="chol-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="chol-no" />
              <Label
                htmlFor="chol-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasHighCholesterol === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="chol-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cardiovascularRiskFactors.yearLabel")}
              </Label>
              <Input
                id="chol-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={highCholesterolYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onHighCholesterolYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>

        {/* Heart Disease */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cardiovascularRiskFactors.heartDiseaseQuestion")}
          </p>
          <RadioGroup
            value={hasHeartDisease?.toString()}
            onValueChange={(val) => onHeartDiseaseChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="heart-yes" />
              <Label
                htmlFor="heart-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="heart-no" />
              <Label
                htmlFor="heart-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasHeartDisease === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="heart-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cardiovascularRiskFactors.yearLabel")}
              </Label>
              <Input
                id="heart-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={heartDiseaseYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onHeartDiseaseYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>
      </CardContent>
    </div>
  );
};
